import {
  formatCountdown,
  getCurrentRound,
  type MarketAsset,
  type Stance
} from '@fukemon/shared';
import { useMemo, useState } from 'react';
import { useAccount, useSignTypedData } from 'wagmi';
import { useClock } from '../hooks/useClock';
import { useGameStore } from '../state/gameStore';
import { PanelShell } from './PanelShell';

const STANCES: Array<{ id: Stance; label: string; mark: string; copy: string }> = [
  { id: 'BULL', label: 'BULL', mark: '↗', copy: 'Price punches up.' },
  { id: 'BEAR', label: 'BEAR', mark: '↘', copy: 'Price eats concrete.' },
  { id: 'CRAB', label: 'CRAB', mark: '↔', copy: 'Price does jack shit.' }
];

const RELAY_TYPES = {
  Forecast: [
    { name: 'player', type: 'address' },
    { name: 'dayId', type: 'uint256' },
    { name: 'roundId', type: 'uint256' },
    { name: 'assetId', type: 'uint256' },
    { name: 'heroId', type: 'bytes32' },
    { name: 'stance', type: 'uint8' },
    { name: 'nonce', type: 'uint256' },
    { name: 'deadline', type: 'uint256' }
  ]
} as const;

export function MarketPanel() {
  const now = useClock();
  const round = useMemo(() => getCurrentRound(now), [now]);
  const [asset, setAsset] = useState<MarketAsset>(round.assets[0]);
  const [stance, setStance] = useState<Stance>('BULL');
  const [status, setStatus] = useState('');
  const { address, isConnected } = useAccount();
  const { signTypedDataAsync, isPending } = useSignTypedData();
  const forecast = useGameStore((state) => state.forecast);
  const heroId = useGameStore((state) => state.selectedHero);
  const submitLocalForecast = useGameStore((state) => state.submitLocalForecast);
  const alreadyEntered = forecast?.dayId === round.dayId;

  const submit = async () => {
    if (!address || !isConnected) {
      setStatus('CONNECT A WALLET FIRST, CHAMP.');
      return;
    }
    if (round.status !== 'commit') {
      setStatus('THIS WINDOW IS LOCKED. THE MARKET GREMLINS ARE COUNTING.');
      return;
    }

    const nonce = Date.now();
    const deadline = Math.floor(Date.now() / 1000) + 600;
    const stanceValue = STANCES.findIndex((item) => item.id === stance);
    setStatus('SIGN THE CALL. NO FUNDS MOVE.');

    try {
      const signature = await signTypedDataAsync({
        domain: {
          name: 'FUKEMON Market Mayhem',
          version: '1',
          chainId: 4663,
          verifyingContract:
            (import.meta.env.VITE_SEASON_CONTRACT as `0x${string}` | undefined) ??
            '0x0000000000000000000000000000000000000000'
        },
        types: RELAY_TYPES,
        primaryType: 'Forecast',
        message: {
          player: address,
          dayId: BigInt(round.dayId),
          roundId: BigInt(round.id),
          assetId: BigInt(asset.id),
          heroId: `0x${Array.from(new TextEncoder().encode(heroId))
            .map((value) => value.toString(16).padStart(2, '0'))
            .join('')
            .padEnd(64, '0')}` as `0x${string}`,
          stance: stanceValue,
          nonce: BigInt(nonce),
          deadline: BigInt(deadline)
        }
      });

      const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
      if (apiUrl) {
        const response = await fetch(`${apiUrl}/relay/forecast`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            forecast: {
              player: address,
              dayId: round.dayId,
              roundId: round.id,
              assetId: asset.id,
              heroId,
              stance,
              nonce,
              deadline
            },
            signature
          })
        });
        if (!response.ok) throw new Error(await response.text());
      }

      submitLocalForecast(address, round.id, round.dayId, asset.id, stance);
      setStatus('CALL LOCKED. YOUR DIGNITY IS NOW ONCHAIN.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Wallet rejected the call.';
      setStatus(message.includes('rejected') ? 'SIGNATURE REJECTED. COWARDICE DETECTED.' : message);
    }
  };

  return (
    <PanelShell eyebrow={`ROUND ${round.id} // ${round.status}`} title="MAKE TODAY’S TERRIBLE CALL" wide>
      <div className="round-bar">
        <div>
          <span>{round.status === 'commit' ? 'CALLS LOCK IN' : 'ROUND RESOLVES IN'}</span>
          <strong>
            {formatCountdown(round.status === 'commit' ? round.lockAt : round.resolveAt, now)}
          </strong>
        </div>
        <p>One shot per UTC day. Price feeds settle the argument. Your bags do not affect damage.</p>
      </div>

      <h3 className="section-label">1. PICK YOUR POISON</h3>
      <div className="asset-grid">
        {round.assets.map((item) => (
          <button
            key={item.id}
            className={asset.id === item.id ? 'asset-card asset-card--active' : 'asset-card'}
            onClick={() => setAsset(item)}
          >
            <span>{item.sector}</span>
            <strong>{item.symbol}</strong>
            <small>{item.name}</small>
            <em>FLAT ±{item.flatBandBps} BPS</em>
          </button>
        ))}
      </div>

      <h3 className="section-label">2. CHOOSE A STANCE</h3>
      <div className="stance-grid">
        {STANCES.map((item) => (
          <button
            key={item.id}
            className={stance === item.id ? 'stance-card stance-card--active' : 'stance-card'}
            onClick={() => setStance(item.id)}
          >
            <span>{item.mark}</span>
            <strong>{item.label}</strong>
            <small>{item.copy}</small>
          </button>
        ))}
      </div>

      <div className="forecast-summary">
        <span>YOUR CALL</span>
        <strong>
          {asset.symbol} GOES {stance}
        </strong>
        <small>Signed forecast only. No approvals. No asset transfer. No financial advice.</small>
      </div>
      <button
        className="primary-button primary-button--wide"
        disabled={isPending || alreadyEntered || round.status !== 'commit'}
        onClick={submit}
      >
        {alreadyEntered
          ? 'TODAY’S DISASTER IS ALREADY LOCKED'
          : isPending
            ? 'WAITING FOR WALLET…'
            : 'SIGN & LOCK CALL'}
      </button>
      {status && (
        <p className="panel-status" role="status" aria-live="polite">
          {status}
        </p>
      )}
    </PanelShell>
  );
}

